
import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CancelButton,
  SaveButton
} from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../../store';
import { createConfig, updateConfig } from '../../../../../modules/masterdata/actions';
import { IConfig } from '../../../../../modules/masterdata/types';
import { selectCreateConfigSuccess, selectUpdateConfigSuccess } from '../../../../../modules/masterdata/selectors';
import { selectOrgProfile } from '../../../../../modules/orgProfile/selectors';

interface ConfigModalProps {
  open: boolean;
  onClose: () => void;
  config?: IConfig | null; // If provided, we are in Edit mode
}

const ConfigModal: React.FC<ConfigModalProps> = ({ open, onClose, config }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const isCreateSuccess = useSelector(selectCreateConfigSuccess);
  const isUpdateSuccess = useSelector(selectUpdateConfigSuccess);
  const orgProfile = useSelector(selectOrgProfile);

  useEffect(() => {
    if (config) {
      setKey(config.key);
      if (typeof config.value === 'object' && config.value !== null) {
        setValue(JSON.stringify(config.value, null, 2));
      } else {
        setValue(String(config.value));
      }
    } else {
      setKey('');
      setValue('');
    }
    setJsonError(null);
  }, [config, open]);

  useEffect(() => {
    if (isCreateSuccess) {
      onClose();
    }
    if (isUpdateSuccess) {
      onClose();
    }
  }, [isCreateSuccess, isUpdateSuccess, onClose]);

  const validateJson = (val: string) => {
    if (val.trim().startsWith('{') || val.trim().startsWith('[')) {
      try {
        JSON.parse(val);
        setJsonError(null);
        return true;
      } catch (e) {
        setJsonError('Invalid JSON format');
        return false;
      }
    }
    setJsonError(null);
    return true;
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setValue(val);
    validateJson(val);
  };

  const handleSave = () => {
    if (jsonError) return;
    if (!orgProfile?.org_id) {
      // Should handle this case, maybe show error or just return
      console.error("Organization ID not found in profile");
      // Optionally set an error state here to show in UI
      return;
    }

    let parsedValue: string | object = value;
    if (value.trim().startsWith('{') || value.trim().startsWith('[')) {
      try {
        parsedValue = JSON.parse(value);
      } catch (e) {
        setJsonError('Invalid JSON format');
        return;
      }
    }

    // If not JSON object, it stays as string.

    const payload = {
      key,
      value: parsedValue,
      org: orgProfile.org_id
    };

    if (config) {
      // Update
      dispatch(updateConfig(payload));
    } else {
      // Create
      dispatch(createConfig(payload));
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        {config ? 'Edit Config' : 'Create Config'}
      </StyledDialogTitle>
      <StyledDialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Key"
          required
          fullWidth
          value={key}
          onChange={(e) => setKey(e.target.value)}
          disabled={!!config} // Disable key editing if it's the identifier, otherwise enable
        />
        <TextField
          margin="dense"
          label="Value"
          required
          fullWidth
          multiline
          rows={5}
          value={value}
          onChange={handleValueChange}
          error={!!jsonError}
          helperText={jsonError || "Enter string or valid JSON object"}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <SaveButton onClick={handleSave} variant="contained" disableElevation disabled={!!jsonError || !key || !value}>
          {config ? 'Save Changes' : 'Create'}
        </SaveButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default ConfigModal;
